import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

const categoryIcons: Record<string, string> = {
  shoes: "ri-footprint-line",
  "hoodies-sweaters": "ri-t-shirt-line",
  "t-shirts": "ri-t-shirt-line",
  jackets: "ri-t-shirt-line",
  "pants-shorts": "ri-t-shirt-line",
  headwear: "ri-user-3-line",
  sets: "ri-stack-line",
  underwear: "ri-t-shirt-line",
  jersey: "ri-t-shirt-line",
  accessories: "ri-glasses-line",
  bags: "ri-handbag-line",
  belts: "ri-link",
  perfumes: "ri-flask-line",
  "other-stuff": "ri-more-line",
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
    setMobileCatOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navLinks = [
    { label: "Strona główna", path: "/" },
    { label: "Kakobuy Spreadsheet", path: "/kakobuy-spreadsheet" },
    { label: "Poradnik zakupowy", path: "/guides" },
    { label: "Blog", path: "/blog" },
    { label: "FAQ", path: "/faq" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <nav className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-950 font-semibold text-lg md:text-xl whitespace-nowrap"
          >
            <span className="text-primary-500 font-bold">Kakobuy</span>
            <span className="text-foreground-950">Spreadsheet</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-5 lg:gap-7">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                {link.label}
              </Link>
            ))}

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                Kategorie
                <i
                  className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                />
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-xl shadow-lg border border-background-200 py-2 z-50 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 text-sm flex-shrink-0">
                        <i className={categoryIcons[cat.slug] || "ri-shopping-bag-3-line"} />
                      </span>
                      <span className="truncate">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-medium px-5 py-2.5 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Kakobuy Spreadsheet
              <i className="ri-external-link-line text-xs" />
            </a>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800"
            aria-label="Menu"
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden pb-5 border-t border-background-200 max-h-[calc(100dvh-64px)] overflow-y-auto">
            <div className="flex flex-col pt-3">
              {/* Main nav links */}
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="flex items-center gap-3 px-2 py-3 text-sm text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors"
                >
                  {link.path === "/" && <i className="ri-home-4-line text-base text-foreground-400" />}
                  {link.path === "/kakobuy-spreadsheet" && <i className="ri-database-2-line text-base text-foreground-400" />}
                  {link.path === "/guides" && <i className="ri-book-open-line text-base text-foreground-400" />}
                  {link.path === "/blog" && <i className="ri-article-line text-base text-foreground-400" />}
                  {link.path === "/faq" && <i className="ri-question-line text-base text-foreground-400" />}
                  {link.label}
                </Link>
              ))}

              {/* Categories section in mobile */}
              <div className="mt-2 pt-3 border-t border-background-200">
                <button
                  onClick={() => setMobileCatOpen(!mobileCatOpen)}
                  className="w-full flex items-center justify-between px-2 py-3 text-sm font-semibold text-foreground-800"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-grid-line text-base text-foreground-400" />
                    Kategorie produktów
                  </span>
                  <i className={`ri-arrow-down-s-line transition-transform text-foreground-400 ${mobileCatOpen ? "rotate-180" : ""}`} />
                </button>
                {mobileCatOpen && (
                  <div className="grid grid-cols-2 gap-1.5 px-2 pb-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.slug}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-2 px-2 py-2.5 text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors"
                      >
                        <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 text-xs flex-shrink-0">
                          <i className={categoryIcons[cat.slug] || "ri-shopping-bag-3-line"} />
                        </span>
                        <span className="truncate text-xs">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* CTA */}
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="mx-2 mt-3 inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 text-sm font-medium px-5 py-3 rounded-full"
              >
                Kakobuy Spreadsheet
                <i className="ri-external-link-line text-xs" />
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}